import { useEffect, useState } from "react";
import {
  BookmarkIcon,
  CalendarDaysIcon,
  CheckIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import {
  useMailboxes,
  useSieveActivate,
  useSieveDelete,
  useSieveGet,
  useSieveList,
  useSieveSave,
  useSieveVacation,
} from "@/api/hooks";
import type { ApiError } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const BLANK = `# Drop your Sieve script here.
# Common snippets:
#
#   require ["fileinto"];
#   if header :contains "Subject" "[spam]" { fileinto "Junk"; stop; }
#
#   require ["vacation"];
#   vacation :days 7 :subject "Away" "Back next week.";
`;

export default function SievePage() {
  const { data: mailboxesData } = useMailboxes();
  const [address, setAddress] = useState<string | null>(null);
  const [script, setScript] = useState<string | null>(null);
  const [newName, setNewName] = useState<string>("");
  const [vacOpen, setVacOpen] = useState(false);
  const [editor, setEditor] = useState<string>("");

  const list = useSieveList(address);
  const scriptData = useSieveGet(address, script);
  const save = useSieveSave();
  const del = useSieveDelete();
  const activate = useSieveActivate();

  const activeMailboxes = mailboxesData?.mailboxes.filter((m) => m.active) ?? [];

  useEffect(() => {
    if (!address && activeMailboxes.length > 0) {
      setAddress(activeMailboxes[0].address);
    }
  }, [address, activeMailboxes]);

  useEffect(() => {
    setScript(null);
  }, [address]);

  useEffect(() => {
    if (scriptData.data?.body !== undefined) {
      setEditor(scriptData.data.body);
    }
  }, [scriptData.data]);

  const handleNew = () => {
    const name = newName.trim();
    if (!name || !/^[A-Za-z0-9_-]{1,64}$/.test(name)) return;
    setScript(name);
    setEditor(BLANK);
    setNewName("");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="text-heading text-3xl font-light sm:text-4xl">
            <span className="text-bitrefinery-red">S</span>ieve
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-bitrefinery-text text-body">
            Per-user filter scripts (Dovecot Pigeonhole). Only the <em>active</em> script
            runs on delivery; the rest are parked. Use the "Vacation" preset to generate an
            autoresponder without touching syntax.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => setVacOpen(true)}
          disabled={!address}
        >
          <CalendarDaysIcon className="h-4 w-4" />
          Vacation preset
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-[260px_1fr]">
        <aside className="space-y-4">
          <div>
            <Label>Mailbox</Label>
            <select
              className="mt-1 flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
              value={address ?? ""}
              onChange={(e) => setAddress(e.target.value || null)}
            >
              {activeMailboxes.map((m) => (
                <option key={m.address} value={m.address}>
                  {m.address}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Scripts</Label>
              <span className="text-xs text-muted-foreground">
                {list.data?.scripts.length ?? 0}
              </span>
            </div>
            <ul className="space-y-1">
              {list.data?.scripts.map((s) => (
                <li key={s.name}>
                  <button
                    type="button"
                    onClick={() => setScript(s.name)}
                    className={
                      "flex w-full items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm transition-colors " +
                      (script === s.name
                        ? "border-bitrefinery-red bg-bitrefinery-red/5"
                        : "border-transparent hover:bg-accent")
                    }
                  >
                    <span className="truncate font-mono">{s.name}</span>
                    {s.active && (
                      <Badge variant="secondary" className="text-[10px]">
                        active
                      </Badge>
                    )}
                  </button>
                </li>
              ))}
              {list.data?.scripts.length === 0 && (
                <li className="text-xs text-muted-foreground">No scripts yet.</li>
              )}
            </ul>

            <div className="flex gap-2 pt-2">
              <Input
                placeholder="new-script"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
              <Button variant="outline" size="icon" onClick={handleNew} title="Create">
                <PlusIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </aside>

        <section className="space-y-3">
          {!address && (
            <div className="rounded-2xl border border-dashed p-10 text-center text-muted-foreground">
              Select a mailbox.
            </div>
          )}
          {address && !script && (
            <div className="rounded-2xl border border-dashed p-10 text-center text-muted-foreground">
              Select or create a script.
            </div>
          )}
          {address && script && (
            <>
              <div className="flex items-center gap-2">
                <h2 className="flex-1 font-mono text-sm">{script}</h2>
                {list.data?.scripts.find((s) => s.name === script)?.active && (
                  <Badge variant="secondary">active</Badge>
                )}
              </div>
              <Textarea
                className="min-h-[320px] font-mono text-xs"
                value={editor}
                onChange={(e) => setEditor(e.target.value)}
              />
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  onClick={async () => {
                    await save.mutateAsync({
                      address,
                      script,
                      body: editor,
                      activate: false,
                    });
                  }}
                  disabled={save.isPending}
                >
                  <CheckIcon className="h-4 w-4" />
                  Save
                </Button>
                <Button
                  variant="outline"
                  onClick={async () => {
                    await save.mutateAsync({
                      address,
                      script,
                      body: editor,
                      activate: true,
                    });
                  }}
                  disabled={save.isPending}
                >
                  <BookmarkIcon className="h-4 w-4" />
                  Save &amp; activate
                </Button>
                <Button
                  variant="ghost"
                  onClick={async () => {
                    await activate.mutateAsync({ address, script });
                  }}
                  disabled={activate.isPending}
                >
                  Activate without saving
                </Button>
                <div className="flex-1" />
                <Button
                  variant="destructive"
                  onClick={async () => {
                    if (confirm(`Delete "${script}"?`)) {
                      await del.mutateAsync({ address, script });
                      setScript(null);
                      setEditor("");
                    }
                  }}
                  disabled={del.isPending}
                >
                  <TrashIcon className="h-4 w-4" />
                  Delete
                </Button>
              </div>
            </>
          )}
        </section>
      </div>

      {vacOpen && address && (
        <VacationDialog address={address} onOpenChange={setVacOpen} />
      )}
    </div>
  );
}

function VacationDialog({
  address,
  onOpenChange,
}: {
  address: string;
  onOpenChange: (open: boolean) => void;
}) {
  const vacation = useSieveVacation();
  const today = new Date().toISOString().slice(0, 10);
  const [subject, setSubject] = useState("Out of office");
  const [body, setBody] = useState(
    "I'm away and will reply when I'm back. For anything urgent, please contact support.",
  );
  const [days, setDays] = useState(7);
  const [start, setStart] = useState<string>(today);
  const [end, setEnd] = useState<string>("");
  const [err, setErr] = useState<string | null>(null);

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Vacation autoresponder</DialogTitle>
          <DialogDescription>
            Writes a Sieve script called <span className="font-mono">vacation</span> and
            activates it. Dovecot will reply once per sender per reply interval.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1.5">
            <Label>Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label>Body</Label>
            <Textarea
              rows={5}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Start (optional)</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>End (optional)</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Reply interval (days)</Label>
            <Input
              type="number"
              min={1}
              max={90}
              value={days}
              onChange={(e) => setDays(Math.max(1, Number(e.target.value) || 7))}
            />
          </div>
        </div>
        {err && <p className="text-xs text-destructive">{err}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              setErr(null);
              try {
                await vacation.mutateAsync({
                  address,
                  subject,
                  body,
                  days,
                  from: address,
                  start: start || undefined,
                  end: end || undefined,
                });
                onOpenChange(false);
              } catch (e) {
                setErr((e as ApiError).code ?? "failed");
              }
            }}
            disabled={vacation.isPending}
          >
            {vacation.isPending ? "Saving…" : "Save & activate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
